import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppRoutes } from './routes';
import { fetchStoreInfo } from './lib/store';
import { UpdateNotification } from './components/ui/update-notification';
import './index.css';
import './styles/stripe-dark-mode.css';

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered: ', registration);
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}

const queryClient = new QueryClient();

function App() {
  useEffect(() => {
    const init = async () => {
      try {
        console.debug('[app] starting store initialization');
        const result = await fetchStoreInfo();
        console.debug('[app] fetchStoreInfo result:', result);
      } catch (err) {
        console.error('[app] fetchStoreInfo error:', err);
      }
    };

    init();
    
    // Handle PWA install prompt
    let deferredPrompt: any;
    
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPrompt = e;
      
      // Show install button after 30 seconds if not already installed
      setTimeout(() => {
        if (deferredPrompt && !window.matchMedia('(display-mode: standalone)').matches) {
          const installBanner = document.createElement('div');
          installBanner.className = 'fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-30 bg-brand-primary text-white p-4 rounded-lg shadow-lg';
          installBanner.innerHTML = `
            <div class="flex items-center justify-between">
              <div>
                <p class="font-medium">Install App</p>
                <p class="text-sm opacity-90">Get the full experience</p>
              </div>
              <div class="flex gap-2">
                <button id="install-btn" class="bg-white text-brand-primary px-3 py-1 rounded text-sm font-medium">Install</button>
                <button id="close-install" class="text-white/80 hover:text-white">×</button>
              </div>
            </div>
          `;
          
          document.body.appendChild(installBanner);
          
          document.getElementById('install-btn')?.addEventListener('click', () => {
            deferredPrompt.prompt();
            deferredPrompt.userChoice.then(() => {
              deferredPrompt = null;
              installBanner.remove();
            });
          });
          
          document.getElementById('close-install')?.addEventListener('click', () => {
            installBanner.remove();
          });
          
          setTimeout(() => {
            if (installBanner.parentNode) {
              installBanner.remove();
            }
          }, 10000);
        }
      }, 30000);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  return (
    <StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <UpdateNotification />
        </QueryClientProvider>
      </HelmetProvider>
    </StrictMode>
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

// Ensure we only create the root once
let root: ReturnType<typeof createRoot>;
if (!(rootElement as any)._reactRoot) {
  root = createRoot(rootElement);
  (rootElement as any)._reactRoot = root;
} else {
  root = (rootElement as any)._reactRoot;
}

root.render(<App />);